/**
 * Back Plan — Tree View
 *
 * Renders Back Plan nodes as a tree rooted at the goal node.
 * Each edge represents "A is a precondition of B" — so children
 * in the tree are prerequisites of their parent.
 *
 * Sibling order follows the stored nodes and edges; the tree does not
 * reorder them (no order field exists for it), so no drag handle is drawn.
 */

import { useState } from 'react';
import { ChevronRight, ChevronDown, Plus, CheckCircle2, Circle, Clock, XCircle } from 'lucide-react';
import type { BackplanNode, BackplanEdge, BackplanNodeStatus } from '../../stores/prioritization/backplanStore';
import { useBackplanStore } from '../../stores/prioritization/backplanStore';
import { PlanNodeWorkspaceItemBadge } from '../planning/PlanNodeWorkspaceItemBadge';
import { isPlanNodeAwaitingFirstSave } from '../../lib/planNodeDraft';

// ── Helpers ─────────────────────────────────────────────────

/** Draft stub prerequisites stay out of list until Save in the panel. */
function visibleChildIds(
  edges: BackplanEdge[],
  toNodeId: string,
  nodes: BackplanNode[],
): string[] {
  return edges
    .filter((e) => e.toNodeId === toNodeId)
    .map((e) => e.fromNodeId)
    .filter((id) => {
      const c = nodes.find((n) => n.id === id);
      return c && !isPlanNodeAwaitingFirstSave(c.text);
    });
}

// ── Types ─────────────────────────────────────────────────────

interface TreeViewProps {
  backplanId: string;
  nodes: BackplanNode[];
  edges: BackplanEdge[];
  onNodeSelect: (nodeId: string) => void;
  selectedNodeId: string | null;
}

// ── Status helpers ────────────────────────────────────────────

function StatusIcon({ status }: { status: BackplanNodeStatus }) {
  switch (status) {
    case 'true':
      return <CheckCircle2 className="h-3.5 w-3.5 text-[var(--green)] flex-shrink-0" />;
    case 'in-progress':
      return <Clock className="h-3.5 w-3.5 text-[var(--mustard)] flex-shrink-0" />;
    case 'not-true':
      return <XCircle className="h-3.5 w-3.5 text-[var(--coral)] flex-shrink-0" />;
    default:
      return <Circle className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />;
  }
}

// ── Node row ──────────────────────────────────────────────────

interface TreeNodeRowProps {
  node: BackplanNode;
  depth: number;
  nodes: BackplanNode[];
  edges: BackplanEdge[];
  onSelect: (id: string) => void;
  isSelected: boolean;
  selectedNodeId: string | null;
}

function TreeNodeRow({
  node,
  depth,
  nodes,
  edges,
  onSelect,
  isSelected,
  selectedNodeId,
}: TreeNodeRowProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const { createNode, createEdge } = useBackplanStore();
  const displayedChildIds = visibleChildIds(edges, node.id, nodes);
  const hasChildren = displayedChildIds.length > 0;

  async function handleAddPrerequisite() {
    const { node: newNode } = await createNode(node.backplanId, {
      text: 'New prerequisite',
      tense: 'future',
      isGoal: false,
    });
    if (!newNode) return;
    await createEdge(node.backplanId, newNode.id, node.id);
    onSelect(newNode.id);
  }

  const childNodes = displayedChildIds
    .map((id) => nodes.find((n) => n.id === id))
    .filter(Boolean) as BackplanNode[];

  return (
    <div>
      <div
        className={`flex items-center gap-1.5 py-1.5 px-2  cursor-pointer group transition-colors
          ${isSelected
            ? 'bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40 text-[var(--text-sec)]'
            : 'hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] text-[var(--text-pri)] dark:text-[var(--text-pri)]'
          }`}
        style={{ paddingLeft: `${depth * 20 + 8}px` }}
        onClick={() => onSelect(node.id)}
      >
        {/* Expand / collapse */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          className={`flex-shrink-0 w-4 h-4 flex items-center justify-center ${hasChildren ? 'visible' : 'invisible'}`}
        >
          {isExpanded ? (
            <ChevronDown className="h-3.5 w-3.5 text-[var(--text-ter)]" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-[var(--text-ter)]" />
          )}
        </button>

        <StatusIcon status={node.status} />

        <PlanNodeWorkspaceItemBadge itemId={node.itemId} className="h-3.5 w-3.5" />

        <span
          className={`flex-1 min-w-0 text-sm truncate ${
            node.isGoal ? 'font-semibold' : 'font-normal'
          }`}
        >
          {node.text || <span className="italic text-[var(--text-ter)]">Untitled</span>}
        </span>

        {node.isGoal && (
          <span className="flex-shrink-0 text-xs bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40 text-[var(--text-sec)] px-1.5 py-0.5 rounded font-medium">
            Goal
          </span>
        )}

        {/* Add prerequisite button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleAddPrerequisite();
          }}
          className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-lift)]"
          title="Add prerequisite"
        >
          <Plus className="h-3 w-3 text-[var(--text-ter)]" />
        </button>
      </div>

      {/* Children */}
      {isExpanded && hasChildren && (
        <>
          {childNodes.map((child) => {
            return (
              <TreeNodeRow
                key={child.id}
                node={child}
                depth={depth + 1}
                nodes={nodes}
                edges={edges}
                onSelect={onSelect}
                isSelected={child.id === selectedNodeId}
                selectedNodeId={selectedNodeId}
              />
            );
          })}
        </>
      )}
    </div>
  );
}

// ── Main TreeView ─────────────────────────────────────────────

export function TreeView({ backplanId, nodes, edges, onNodeSelect, selectedNodeId }: TreeViewProps) {
  const { createNode } = useBackplanStore();

  // Build root nodes: visible goal nodes, else visible nodes with no incoming edges
  const visibleGoals = nodes.filter((n) => n.isGoal && !isPlanNodeAwaitingFirstSave(n.text));
  const visibleOrphans = nodes.filter(
    (n) =>
      !edges.some((e) => e.toNodeId === n.id) && !isPlanNodeAwaitingFirstSave(n.text),
  );
  const rootNodeIds =
    visibleGoals.length > 0 ? visibleGoals.map((n) => n.id) : visibleOrphans.map((n) => n.id);

  const graphHasVisibleNodes = nodes.some((n) => !isPlanNodeAwaitingFirstSave(n.text));

  async function handleAddGoal() {
    await createNode(backplanId, {
      text: 'Desired outcome',
      tense: 'future',
      isGoal: true,
    });
  }

  if (nodes.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-3">
          No nodes yet. Start by adding a goal node — the future state you're planning backward from.
        </p>
        <button
          onClick={handleAddGoal}
          className="flex items-center gap-2 px-3 py-2 bg-[var(--slate)] hover:bg-[var(--slate)] text-white text-sm font-medium transition-colors"
        >
          <Plus className="h-4 w-4" />
          Add Goal Node
        </button>
      </div>
    );
  }

  if (!graphHasVisibleNodes) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center px-4">
        <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] mb-2 max-w-sm">
          Draft nodes aren’t shown in the tree until you save condition text in the{' '}
          <strong className="text-[var(--text-pri)] dark:text-[var(--text-pri)]">Node detail</strong> panel
          on the right.
        </p>
        <p className="text-xs text-[var(--text-ter)]">Use Save (or ⌘↵) on the draft you’re editing.</p>
      </div>
    );
  }

  if (rootNodeIds.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center px-4">
        <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)] max-w-md">
          Nothing visible yet: save drafts in the panel, or mark at least one node as Goal with real
          wording (not placeholder text).
        </p>
      </div>
    );
  }

  return (
    <div className="py-2">
      {rootNodeIds.map((rootId) => {
        const rootNode = nodes.find((n) => n.id === rootId);
        if (!rootNode) return null;
        return (
          <TreeNodeRow
            key={rootId}
            node={rootNode}
            depth={0}
            nodes={nodes}
            edges={edges}
            onSelect={onNodeSelect}
            isSelected={rootNode.id === selectedNodeId}
            selectedNodeId={selectedNodeId}
          />
        );
      })}

      <button
        onClick={handleAddGoal}
        className="mt-3 flex items-center gap-2 px-3 py-2 w-full text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-sec)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] transition-colors border border-dashed border-[var(--rule)]"
      >
        <Plus className="h-4 w-4" />
        Add goal node
      </button>
    </div>
  );
}
